import {
    Modal,
    Notification,
    STATUS_TYPES,
    TableWithHeader,
} from "dyl-components";
import React, { useEffect, useState } from "react";
import CallHistoryToolbar from "./Toolbar";
import BulkActionsProvider from "shared/context/BulkActionsProvider";
import "./index.scss";
import CallHistoryLogTable from "shared/CallHistoryLog/Table";
import {
    KEY_DATE,
    KEY_CONTACT_ACCOUNT,
    KEY_PHONE_NUMBER,
    KEY_USER,
    KEY_DURATION,
    KEY_ACTIONS,
    KEY_DIRECTION,
    KEY_SYSTEM_RESULT,
    KEY_NOTE,
    KEY_CALL_TAGS,
    KEY_DESTINATION,
    KEY_USER_RESULT,
    KEY_CALL_TYPE,
} from "shared/constants/CALL_HISTORY_LOGS_COLUMNS";
import { useNavigate, useSearchParams } from "react-router-dom";
import pbxConfigActions from "actions/pbx_config";
import { useDispatch, useSelector } from "react-redux";
import callHistoryActions from "actions/call_history";
import usersActions from 'actions/users';
import EditCall from "./EditCall";
import SendEmailModal from "shared/modals/SendEmailModal";
import AddTaskModal from "shared/modals/AddTaskModal";
import { useForm } from "react-hook-form";
import useTaskForm from "utils/useTaskForm";
import useModal from "utils/useModal";
import AddContactModal from "shared/modals/AddContactModal";
import { PhoneUtil, RecordUtils } from "utils";
import useEventForm from "utils/useEventForm";
import AddEventModal from "shared/modals/AddEventModal";
import eventActions from "actions/event";
import Utils from "shared/EventForm/Utils";
import eventAttendeeActions from "actions/event_attendee";
import uploadActions from "actions/upload";
import { FILE_CATEGORIES } from "utils/FileUtils";
import eventAttachmentActions from "actions/event_attachment";
import groupsActions from "actions/groups";
import tagsCategoryActions from "actions/tag_categories";
import relationshipsActions from 'actions/relationships';
import { Icon, Loader } from "semantic-ui-react";
 

const CallHistoryLog = ({ root }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const columnDisplayOptions = [
        KEY_DATE,
        KEY_CONTACT_ACCOUNT,
        KEY_PHONE_NUMBER,
        KEY_USER,
        KEY_DURATION,
        KEY_ACTIONS,
        KEY_DIRECTION,
        KEY_SYSTEM_RESULT,
        KEY_NOTE,
        KEY_CALL_TAGS,
        KEY_DESTINATION,
        KEY_USER_RESULT,
        KEY_CALL_TYPE
    ];
    const defaultColumnsDisplayed = [
        KEY_DATE,
        KEY_CONTACT_ACCOUNT,
        KEY_PHONE_NUMBER,
        KEY_USER,
        KEY_DURATION,
        KEY_ACTIONS,
        KEY_DIRECTION,
        KEY_SYSTEM_RESULT,
        KEY_DESTINATION,
        KEY_NOTE,
    ];
    const columnsParameter =
        params
            .get("columns")
            ?.split(",")
            ?.filter((column) => columnDisplayOptions.includes(column)) || [];
    const columns = columnsParameter.length
        ? columnsParameter
        : defaultColumnsDisplayed;

    const {
        task_labels,
        organizer,
        organizer_email,
        organizer_id,
        current_user,
        accountContacts,
        contactsInHotlistOfficeView,
        listOfTagCategories,
        relationships,
        officeViewCreds
    } = useSelector((state) => {
        const {
            first_name = "",
            last_name = "",
            email: organizer_email,
        } = state?.users?.userProfile;
        return {
            task_labels: state?.task?.task_type_labels?.map?.(
                ({ name, id }) => ({
                    key: id,
                    value: id,
                    text: name,
                })
            ),
            organizer_id: state?.auth?.user_id,
            organizer: `${first_name} ${last_name}`,
            current_user: state?.auth,
            savedSettingsRoute : state.app.savedSettingsRoute,
            accountContacts: state?.account?.contactIds?.map?.(
                ({ first_name, last_name, email, suffix, id }) => ({
                    text: `${first_name || ""} ${last_name || ""}${
                        suffix ? `, ${suffix}` : ""
                    }`,
                    email,
                    key: id,
                    value: id,
                })
            ),
            contactsInHotlistOfficeView:
                state?.groups?.contactsInHotlistOfficeView,
            listOfTagCategories: state?.tag_categories?.listOfTagCategories,
            organizer_email,
            relationships: state.relationships.relationships.map(RecordUtils.getRelationOption),
            officeViewCreds: state.officeView.creds
        };
    });

        
    const isPhoneSystemSupported = PhoneUtil.isPhoneSystemSupported(officeViewCreds);
    
    const addContactModal = useModal(),
        editLogModal = useModal(),
        emailModal = useModal(),
        taskModal = useModal(),
        eventModal = useModal();

    const {
        control,
        reset: resultsReset,
        handleSubmit,
        formState: { isValid: resultsIsValid, isDirty: resultsIsDirty },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            call_result: editLogModal?.data?.user_result ?? "",
            call_notes: editLogModal?.data?.note ?? "",
            call_tag: null,
            related_to: null,
            sub_tag: null,
        },
        reValidateMode: "all",
    });

    const {
        control: taskControl,
        watch: taskWatch,
        isValid: taskIsValid,
        isDirty: taskIsDirty,
        trigger: taskTrigger,
        setValue: taskSetValue,
        getValues: taskGetValues,
        clearErrors: taskClearErrors,
        setError: taskSetError,
        reset: taskReset,
        taskBeingEdited,
        loadTask,
        addTask,
        state,
        setState,
        onDelete: onDeleteTask,
        onUpdate: onUpdateTask,
        handleSubmit: handleSubmitTask,
        isAllowedToModify: isTaskAllowedToModify,
    } = useTaskForm({
        isContact: false,
        isModal: true,
        open: taskModal?.isVisible,
    });

    const {
        control: eventControl,
        watch: eventWatch,
        isValid: eventIsValid,
        isDirty,
        trigger: eventTrigger,
        setValue: eventSetValue,
        getValues,
        reset,
        isAllowedToModify,
        eventBeingEdited,
        loadEvent,
        handleSubmit: handleSubmitEvent,
        resetField,
    } = useEventForm({ contact_id: editLogModal?.data?.record?.contact_id });

    const { count, isLoading, data } = useSelector((state) => ({
        count: state?.call_history?.call_history?.count,
        isLoading: state?.call_history?.isReadingCallHistory,
        data: state?.call_history?.call_history?.data,
    }));

    const isColumnHidden = (column) => {
        return !columns.includes(column);
    };

    const onToggleColumn = async (column) => {
        const updatedColumns = isColumnHidden(column)
            ? [...columns, column]
            : columns.filter((visibleColumn) => visibleColumn !== column);

        const updateColumnsParameter = (filtersToRemove) => {
            const query = new URLSearchParams(params);
            if (updatedColumns.length) {
                query.set("columns", updatedColumns.join(","));
            } else {
                query.delete("columns");
            }
            if (filtersToRemove) {
                const filters = Array.isArray(filtersToRemove) ? filtersToRemove : [filtersToRemove];
                filters.forEach(f => query.delete(f));
            }
            const query_string = query.toString();
            navigate(
                `${root}/call-history${query_string ? `?${query_string}` : ""}`
            );
        };

        updateColumnsParameter();
    };

    const onPageChange = (_, { activePage }) => {
        const query = new URLSearchParams(params);
        query.set("page", activePage);
        const query_string = query.toString();
        navigate(
            `${root}/call-history${query_string ? `?${query_string}` : ""}`
        );
    };

    const onSaveResults = async (data) => {
        const uuid = editLogModal?.data?.call_uuid;
        const body = {
            notes: data.call_notes || "",
            result: data.call_result || null,
            tag: data?.sub_tag,
            related_id: data?.related_to || null
        };
        try {
            await dispatch(callHistoryActions.setUserResult(uuid, body));
            resultsReset(null, { keepValues: true });
            Notification.alert(
                "Successfully updated Call Results!",
                STATUS_TYPES.SUCCESS
            );
            dispatch(callHistoryActions.getCalls(params));
            editLogModal?.closeModal?.();
        } catch (error) {
            console.log(error);
            Notification.alert(
                "Failed to update Call Results",
                STATUS_TYPES.ERROR
            );
        }
    };

    const onMarkVoicemail = (call_uuid, value, refetch = true) => {
        if (!call_uuid) return;
        try {
            dispatch(
                callHistoryActions.setVoicemailListened(
                    null,
                    { listened: value },
                    { call_uuid }
                )
            );
            Notification.alert(
                `Marked successfully ${value ? "listened" : "unlistened"}`,
                STATUS_TYPES.SUCCESS
            );
            if (refetch) dispatch(callHistoryActions.getCalls(params));
        } catch (error) {
            Notification.alert("Failed to update", STATUS_TYPES.ERROR);
            console.log(error);
        }
    };

    const onReportCall = (call, payload) => {
        if (!call?.call_uuid) return;
        try {
            dispatch(
                callHistoryActions.reportCall(
                    {
                        notes: "",
                        problem: payload?.problem_type,
                    },
                    { call_uuid: call?.call_uuid }
                )
            );
            dispatch(callHistoryActions.getCalls(params.toString()));
        } catch (error) {
            console.log(error);
        }
    };

    const onAddContact = (call) => {
        addContactModal?.openModal({
            ...call,
            phone: PhoneUtil.sanitizePhoneNumber(call?.phone),
        });
    };

    const onTaskClick = () => {
        loadTask({
            contact: editLogModal?.data?.record?.contact_id,
        });
        taskModal?.openModal?.();
    };

    const onEventClick = () => {
        loadEvent(null);
        eventModal?.openModal();
    };

    const onUpload = (file, file_type) => {
        return dispatch(uploadActions.uploadFiles(file, file_type));
    };

    const onAddAttachments = (attachments, user_id, event_id) => {
        return dispatch(
            eventAttachmentActions.addAttachments(
                attachments,
                { user_id },
                event_id
            )
        );
    };

    const addAttachments = async (files, event_id) => {
        if (files && files.length > 0) {
            const toUpload = files.filter((file) => file.id === undefined);
            const toCopy = files
                .filter((file) => file.id)
                .map((file) => ({
                    file_id: file.file_id,
                    name: file.name,
                    size: file.size,
                }));
            const uploadedFiles = await onUpload(
                toUpload,
                FILE_CATEGORIES.EVENT
            );
            const newFiles = toUpload.map((_, idx) => {
                return {
                    name: files[idx].name,
                    file_id: uploadedFiles[idx],
                    size: files[idx].size,
                };
            });
            return onAddAttachments(
                [...newFiles, ...toCopy],
                current_user.user_id,
                event_id
            );
        }
        return Promise.resolve();
    };

    const onAddAttendees = (event_id, params) => {
        return dispatch(
            eventAttendeeActions.addAttendees(params, null, event_id)
        );
    };

    const addAttendees = (event_id, usersToAdd) => {
        let params = {
            payload: usersToAdd,
            integration_id: null,
            access_token: null,
        };
        if (usersToAdd.length > 0) {
            return onAddAttendees(event_id, params);
        }
        return Promise.resolve();
    };

    const onAddEvent = async (event) => {
        const id = await dispatch(
            eventActions.addEvent(Utils.toPayload(event))
        );
        return id;
    };

    const addEvent = async () => {
        const values = getValues();
        const {
            all_day,
            start_date,
            start_time,
            end_date,
            end_time,
            users,
            attachments,
            contacts,
            timezone,
            related_to,
        } = values;

        const attendees = [
            ...contacts.map((contact_id) => ({
                accepted: "no",
                contact_id,
                emailed: false,
            })),
            ...users.map((user_id) => ({
                accepted: "no",
                user_id,
                emailed: false,
            })),
        ];

        // TODO: add recurring event properties
        const payload = {
            complete: false,
            conference_phone: values.phone_number,
            conference_pin: values.pin,
            conference_url: values.conference_line,
            content: values.content,
            content_type: "text/html",
            label: values.label ? values.label : 0,
            location: values.location,
            name: values.name,
            all_day,
            start_date,
            start_time,
            end_date,
            end_time,
            // "recurring_event_id": 0,
            organizer_id,
            organizer,
            organizer_email,
            users: values.users,
            contact_id: values.contacts,
            timezone,
            related_to,
        };

        try {
            const { id } = await onAddEvent(payload);
            await addAttendees(id, attendees);
            await addAttachments(attachments, id, "pdf");
            eventModal?.closeModal();
            Notification.alert(
                "Successfully created event!",
                STATUS_TYPES.SUCCESS
            );
        } catch (e) {
            console.log(e);
            Notification.alert("Failed to create event", STATUS_TYPES.ERROR);
        }
    };

    const onHotlistClick = async () => {
        try {
            if (
                contactsInHotlistOfficeView.includes(
                    editLogModal?.data?.record?.contact_id
                )
            ) {
                await dispatch(
                    groupsActions.removeHotlist(
                        editLogModal?.data?.record?.contact_id
                    )
                );
                await dispatch(
                    groupsActions.checkHotlistOfficeView({
                        contacts: [editLogModal?.data?.record?.contact_id],
                    })
                );
                Notification.alert(
                    "Successfully removed contact from hotlist!",
                    STATUS_TYPES.SUCCESS,
                    true
                );
            } else {
                await dispatch(
                    groupsActions.addToHotlist({
                        contact_id: [editLogModal?.data?.record?.contact_id],
                    })
                );
                await dispatch(
                    groupsActions.checkHotlistOfficeView({
                        contacts: [editLogModal?.data?.record?.contact_id],
                    })
                );
                Notification.alert(
                    "Successfully added contact to hotlist!",
                    STATUS_TYPES.SUCCESS,
                    true
                );
            }
        } catch (error) {
            console.log(error);
            if (
                contactsInHotlistOfficeView.includes(
                    editLogModal?.data?.record?.contact_id
                )
            ) {
                Notification.alert(
                    "Problem removing contact from hotlist!",
                    STATUS_TYPES.ERROR,
                    true
                );
            } else {
                Notification.alert(
                    "Problem adding contact to hotlist!",
                    STATUS_TYPES.ERROR,
                    true
                );
            }
        }
    };

    const [isLoadingCallLog, setIsLoadingCallLog] = useState(false);

    const onEditLog = (call) => {
        if (!call?.call_uuid) return;
        setIsLoadingCallLog(true);
        if (call?.record?.contact_type === "person") {
            dispatch(relationshipsActions.getContactRelations({ person_id: call?.record?.contact_id }));
        }
        dispatch(
            callHistoryActions.getCallDetails(
                call?.call_uuid,
                call?.direction === "internal" && { user_id: true }
            )
        ).then(logResponse => {
            const callTag = call?.tags?.[0] || "";
            const [parent_value] = (callTag?.name || "").split("/");
            const parent_id = listOfTagCategories?.find?.(
                (item) => item?.category === parent_value
            )?.id;

            resultsReset({
                call_notes: call?.note,
                call_result: call?.user_result,
                call_tag: parent_id,
                sub_tag: callTag?.id,
                related_to: logResponse?.related_id || null,
            });
            const { account_id, is_customer, pipeline_id, pipeline } = logResponse?.contacts?.[0] || {};
            editLogModal?.openModal({
                ...call,
                ...(is_customer && account_id ? {
                    customer_info: {
                        account_id,
                        pipeline_id,
                        pipeline
                    }
                } : {})
            });
        }).finally(() => {
            setIsLoadingCallLog(false);
        });
    };

    const onClickToDial = () => {
        const { contact_id, account_id } = editLogModal?.data?.record || {};
        const { phone: destination } = editLogModal?.data || {}
        editLogModal?.closeModal?.();
        PhoneUtil.C2D({payload: { contact_id, account_id, destination }, dispatch});
    }

    useEffect(() => {
        dispatch(pbxConfigActions.getDestinations());
        dispatch(usersActions.readBulkUsernamesCallHistory({ limit: 1000, active: true }))
        dispatch(tagsCategoryActions.getListOfTagCategories());
    }, [dispatch]);

    useEffect(() => {
        dispatch(callHistoryActions.getCalls(params));
    }, [dispatch, params]);

    const isEditingCallLogForContact = editLogModal?.data?.record?.contact_type === "person";

    return (
        <div className="CallHistoryLogPage">
            <TableWithHeader
                header={
                    <CallHistoryToolbar
                        isColumnHidden={isColumnHidden}
                        onToggleColumn={onToggleColumn}
                    />
                }
                table={
                    <CallHistoryLogTable
                        isColumnHidden={isColumnHidden}
                        onPageChange={onPageChange}
                        onEditLog={onEditLog}
                        onAddContact={onAddContact}
                        onMarkVoicemail={onMarkVoicemail}
                        onReportCall={onReportCall}
                        count={count}
                        isLoading={isLoading} 
                        data={data}
                        onClickToDial={(payload) => PhoneUtil.C2D({payload, dispatch})}
                        isPhoneSystemSupported={isPhoneSystemSupported}
                    />
                }
            />
            <Modal
                open={addContactModal?.isVisible}
                onClose={addContactModal?.closeModal}
            >
                <AddContactModal
                    isOfficeView
                    unknownNumber={addContactModal?.data?.phone}
                    onClose={addContactModal?.closeModal}
                    onSuccessCallback={() =>
                        dispatch(callHistoryActions.getCalls(params))
                    }
                />
            </Modal>
            <Modal
                open={isLoadingCallLog || editLogModal?.isVisible}
                onClose={editLogModal?.closeModal}
                className="EditCallResult"
            >
                <Modal.Content>
                    {!isLoadingCallLog ? (
                        <EditCall
                            onNewContactClick={onAddContact}                                 
                            editLogModal={editLogModal}
                            control={control}
                            onSave={handleSubmit(onSaveResults)}
                            onSaveDisabled={!resultsIsDirty || !resultsIsValid}
                            onEmailClick={emailModal?.openModal}
                            onTaskClick={onTaskClick}
                            onEventClick={onEventClick}
                            onHotlistClick={onHotlistClick}
                            isInHotlist={contactsInHotlistOfficeView.includes(
                                editLogModal?.data?.record?.contact_id
                            )}
                            onClickToDial={onClickToDial}
                            relatedToOptions={isEditingCallLogForContact ? relationships : (() => {
                                const { account_id, pipeline_id, pipeline } = editLogModal?.data?.customer_info || {};
                                if (account_id && pipeline_id && pipeline) {
                                    return [
                                        {
                                            key: pipeline_id,
                                            value: pipeline_id,
                                            text: pipeline,
                                            content: <span><Icon className='fas fa-crown' /> {pipeline}</span>
                                        }
                                    ]
                                }
                                return [];
                            })()}
                        />
                    ) : <Loader active inline="centered" />}
                </Modal.Content>
            </Modal>
            <SendEmailModal
                open={emailModal?.isVisible}
                onClose={emailModal?.closeModal}
                contact_id={editLogModal?.data?.record?.contact_id}
                account_id={editLogModal?.data?.record?.account_id}
                isAccountModal={!editLogModal?.data?.record?.contact_id}
            />
            <AddTaskModal
                open={taskModal?.isVisible}
                onClose={taskModal?.closeModal}
                state={state}
                control={taskControl}
                watch={taskWatch}
                isValid={taskIsValid}
                isDirty={taskIsDirty}
                trigger={taskTrigger}
                setValue={taskSetValue}
                getValues={taskGetValues}
                clearErrors={taskClearErrors}
                setError={taskSetError}
                reset={taskReset}
                taskBeingEdited={taskBeingEdited}
                loadTask={loadTask}
                addTask={addTask}
                setState={setState}
                onDelete={onDeleteTask}
                onUpdate={onUpdateTask}
                task_labels={task_labels}
                organizer_id={organizer_id}
                organizer={organizer}
                organizer_email={organizer_email}
                handleSubmit={handleSubmitTask}
                onRefresh={taskModal?.closeModal}
                email={{ body: "", subject: "" }}
                isAllowedToModify={isTaskAllowedToModify}
                hiddenContactSelector
            />
            <AddEventModal
                open={eventModal?.isVisible}
                onClose={eventModal?.closeModal}
                onSave={handleSubmitEvent(addEvent)}
                selected_users={[]}
                isAllowedToModify={isAllowedToModify}
                control={eventControl}
                watch={eventWatch}
                isValid={eventIsValid}
                isDirty={isDirty}
                trigger={eventTrigger}
                setValue={eventSetValue}
                getValues={getValues}
                reset={reset}
                resetField={resetField}
                eventBeingEdited={eventBeingEdited}
                setNotificationContactName={() => {}}
                accountContacts={accountContacts}
                currentContact={{
                    text: editLogModal?.data?.record?.name || "1",
                    key: editLogModal?.data?.record?.contact_id || "",
                    value: editLogModal?.data?.record?.contact_id || "",
                    email: editLogModal?.data?.record?.email || "",
                }}
            />
        </div>
    );
};

export default function CallHistoryContainer({ root = "/settings" }) {
    return (
        <BulkActionsProvider>
            <CallHistoryLog root={root} />
        </BulkActionsProvider>
    );
}
